import React, { useState } from 'react';
import { Users } from 'lucide-react';
import { formatCurrency, formatDate } from '../../../utils/helpers';

const TournamentPlayerSummary = ({
  selectedTournament,
  tournaments,
  games,
  players,
  expenses,
  filteredExpenses,
  walletTransactions,
  formatCurrency: formatCurrencyProp
}) => {
  const [expandedPlayers, setExpandedPlayers] = useState({});

  const format = formatCurrencyProp || formatCurrency;

  // IMPORTANT: Only use actual expenses, NOT Initial Donations
  const actualExpensesOnly = filteredExpenses.filter(e => !e.isRegistrationFee);

  // Get selected tournament details
  const tournament = tournaments?.find(t => t.id === selectedTournament) || null;

  // Get all games in selected tournament (or all games if "all" selected)
  const tournamentGames = selectedTournament === 'all'
    ? games
    : games.filter(g => g.tournamentId === selectedTournament);

  // Calculate per-player tournament expenses
  const calculatePlayerTournamentExpenses = () => {
    const playerExpenses = {};

    // Find the settled tournament fee for this tournament
    const tournamentFee = expenses.find(e =>
      e.isTournamentFee &&
      e.tournamentId === selectedTournament &&
      e.settled &&
      e.splitAmongDetails?.length > 0
    );

    players.forEach(player => {
      // Find all games this player participated in
      const playerGames = tournamentGames.filter(game =>
        game.playing11 && game.playing11.includes(player.id)
      );

      let totalOwed = 0;
      let tournamentFeeShare = 0;
      let gameExpensesShare = 0;
      const gameBreakdown = [];

      // 1. Get tournament fee share from splitAmongDetails
      if (tournamentFee) {
        const playerTournamentShare = tournamentFee.splitAmongDetails.find(p =>
          p.oderId === player.id ||
          p.oderId === player.oderId ||
          p.oderId === player.userId
        );
        if (playerTournamentShare) {
          tournamentFeeShare = playerTournamentShare.amount || 0;
          totalOwed += tournamentFeeShare;
        }
      }

      // 2. Calculate game expenses (snacks, etc.)
      playerGames.forEach(game => {
        // Use actualExpensesOnly - exclude tournament fees
        const gameExpenses = actualExpensesOnly.filter(exp =>
          exp.gameId === game.id && !exp.isTournamentFee
        );
        const totalGameCost = gameExpenses.reduce((sum, exp) => sum + exp.amount, 0);
        const isLate = game.lateArrivals?.[player.id];

        if (totalGameCost > 0 || isLate) {
          const squadSize = game.playing11?.length || 0;
          const superSubCount = game.superSubs?.length || 0;
          const regularPlayerCount = squadSize - superSubCount;
          const isSuperSub = game.superSubs?.includes(player.id);
          const LATE_FEE = game.lateFeeAmount ?? 5;

          // Calculate player's share
          let playerShare = 0;
          if (squadSize > 0) {
            const totalShares = regularPlayerCount + (superSubCount * 0.5);
            const perPlayerCost = totalShares > 0 ? totalGameCost / totalShares : 0;
            playerShare = isSuperSub ? perPlayerCost * 0.5 : perPlayerCost;
          }

          // Add late fee if player was late
          if (isLate) {
            playerShare += LATE_FEE;
          }

          gameExpensesShare += playerShare;
          totalOwed += playerShare;
          gameBreakdown.push({
            game,
            gameExpenses,
            playerShare,
            isSuperSub,
            isLate,
            totalGameCost
          });
        }
      });

      // Only include players who participated in the tournament
      if (playerGames.length > 0 || tournamentFeeShare > 0) {
        // Calculate how much player paid upfront for THIS tournament's games
        const playerPaidExpenses = expenses.filter(exp =>
          exp.addedByPlayerId === player.id &&
          exp.approvalStatus === 'approved' &&
          !exp.isRegistrationFee &&
          !exp.isTournamentFee &&
          tournamentGames.some(g => g.id === exp.gameId)
        );
        const paidUpfront = playerPaidExpenses.reduce((sum, exp) => sum + exp.amount, 0);

        // Calculate reimbursements - ONLY actual reimbursement transactions
        const playerReimbursements = walletTransactions.filter(txn =>
          txn.userId === player.id &&
          txn.type === 'reimbursement' &&
          txn.status === 'approved' &&
          txn.amount > 0
        );
        const reimbursed = playerReimbursements.reduce((sum, txn) => sum + txn.amount, 0);

        // Balance = Total Owed - (Paid Upfront - Reimbursed)
        const netPaidUpfront = paidUpfront - reimbursed;
        const balance = totalOwed - netPaidUpfront;

        playerExpenses[player.id] = {
          player,
          gamesPlayed: playerGames.length,
          totalGames: tournamentGames.length,
          totalOwed,
          tournamentFeeShare,
          gameExpensesShare,
          paidUpfront,
          reimbursed,
          balance,
          gameBreakdown
        };
      }
    });

    return Object.values(playerExpenses);
  };

  const playerExpenses = calculatePlayerTournamentExpenses();

  // Calculate summary stats
  const tournamentFeeTotal = expenses.find(e =>
    e.isTournamentFee &&
    e.tournamentId === selectedTournament &&
    e.settled
  )?.amount || 0;
  const totalGames = tournamentGames.length;
  const totalPlayers = playerExpenses.length;
  const avgParticipation = totalPlayers > 0
    ? playerExpenses.reduce((sum, p) => sum + p.gamesPlayed, 0) / totalPlayers
    : 0;
  const gameExpensesTotal = actualExpensesOnly.reduce((sum, exp) => sum + exp.amount, 0);

  // Calculate total late fees to add to expenses
  let totalLateFees = 0;
  tournamentGames.forEach(game => {
    if (game.lateArrivals) {
      Object.keys(game.lateArrivals).forEach(playerId => {
        if (game.lateArrivals[playerId]) {
          totalLateFees += game.lateFeeAmount ?? 5;
        }
      });
    }
  });

  const totalExpenses = gameExpensesTotal + tournamentFeeTotal + totalLateFees;
  const avgPerGame = totalGames > 0 ? totalExpenses / totalGames : 0;
  const avgPerPlayer = totalPlayers > 0 ? totalExpenses / totalPlayers : 0;
  const fullyPaid = playerExpenses.filter(p => Math.abs(p.balance) < 0.01).length;
  const receivablesTotal = playerExpenses.reduce((sum, p) => sum + Math.max(0, p.balance), 0);

  return (
    <div className="space-y-6">
      {/* Tournament Header */}
      {tournament && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-2xl font-bold text-purple-900 mb-2">
                🏆 {tournament.name}
              </h3>
              <div className="flex gap-4 text-sm text-purple-700">
                <span>📅 {formatDate(tournament.startDate)}</span>
                {tournament.endDate && (
                  <>
                    <span>→</span>
                    <span>{formatDate(tournament.endDate)}</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                      Completed ✓
                    </span>
                  </>
                )}
                {!tournament.endDate && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                    Ongoing
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border-2 border-blue-200 rounded-xl p-5">
          <p className="text-sm text-blue-700 font-semibold mb-1">Tournament Overview</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Games:</span>
              <span className="font-bold">{totalGames}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Players:</span>
              <span className="font-bold">{totalPlayers}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Avg Participation:</span>
              <span className="font-bold">{avgParticipation.toFixed(1)} games</span>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-green-200 rounded-xl p-5">
          <p className="text-sm text-green-700 font-semibold mb-1">Financial Summary</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Expenses:</span>
              <span className="font-bold">{format(totalExpenses)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Avg per Game:</span>
              <span className="font-bold">{format(avgPerGame)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Avg per Player:</span>
              <span className="font-bold">{format(avgPerPlayer)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-purple-200 rounded-xl p-5">
          <p className="text-sm text-purple-700 font-semibold mb-1">Payment Status</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Fully Paid:</span>
              <span className="font-bold text-green-600">{fullyPaid} ({totalPlayers > 0 ? Math.round((fullyPaid / totalPlayers) * 100) : 0}%)</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">With Balance:</span>
              <span className="font-bold text-orange-600">{totalPlayers - fullyPaid}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Receivables:</span>
              <span className="font-bold text-red-600">{format(receivablesTotal)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Player Expenses Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200 p-4">
          <h3 className="text-lg font-bold text-gray-900">
            Player Expenses Summary
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            Total Owed = Tournament Fee Share + Game Expenses (Snacks, etc.)
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Player</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Games</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Owed</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid Upfront</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {playerExpenses
                .sort((a, b) => b.balance - a.balance)
                .map(playerData => (
                  <React.Fragment key={playerData.player.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        {playerData.player.name}
                      </td>
                      <td className="px-4 py-4 text-sm text-center">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold">
                          {playerData.gamesPlayed}/{totalGames}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="text-sm font-semibold text-orange-600">
                          {format(playerData.totalOwed)}
                        </div>
                        {(playerData.tournamentFeeShare > 0 || playerData.gameExpensesShare > 0) && (
                          <div className="text-xs text-gray-500">
                            {playerData.tournamentFeeShare > 0 && `T: ${format(playerData.tournamentFeeShare)}`}
                            {playerData.tournamentFeeShare > 0 && playerData.gameExpensesShare > 0 && ' + '}
                            {playerData.gameExpensesShare > 0 && `G: ${format(playerData.gameExpensesShare)}`}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-right">
                        {playerData.paidUpfront > 0 ? (
                          <span className="text-blue-600 font-semibold">
                            {format(playerData.paidUpfront)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                        {playerData.reimbursed > 0 && (
                          <div className="text-xs text-gray-500">
                            (Reimb: {format(playerData.reimbursed)})
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-right">
                        <span className={`px-3 py-1 rounded ${Math.abs(playerData.balance) < 0.01
                          ? 'bg-gray-100 text-gray-600'
                          : playerData.balance > 0
                            ? 'bg-red-100 text-red-700'
                            : 'bg-green-100 text-green-700'
                          }`}>
                          {format(Math.abs(playerData.balance))}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {Math.abs(playerData.balance) < 0.01 ? (
                          <span className="text-xs text-gray-500">Settled</span>
                        ) : playerData.balance > 0 ? (
                          <span className="text-xs text-red-600 font-semibold">Owes</span>
                        ) : (
                          <span className="text-xs text-green-600 font-semibold">Credit</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={() => setExpandedPlayers(prev => ({
                            ...prev,
                            [playerData.player.id]: !prev[playerData.player.id]
                          }))}
                          className="text-purple-600 hover:text-purple-800 font-semibold text-sm"
                        >
                          {expandedPlayers[playerData.player.id] ? '▼ Hide' : '▶ Show'}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Game Breakdown */}
                    {expandedPlayers[playerData.player.id] && (
                      <tr>
                        <td colSpan="7" className="bg-gray-50 px-6 py-4">
                          <div className="space-y-3">
                            {/* Tournament Fee Summary */}
                            {playerData.tournamentFeeShare > 0 && (
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-semibold text-purple-900">🏆 Tournament Fee Share</p>
                                    <p className="text-xs text-purple-700">
                                      Based on {playerData.gamesPlayed} game{playerData.gamesPlayed !== 1 ? 's' : ''} played
                                    </p>
                                  </div>
                                  <p className="text-lg font-bold text-purple-600">
                                    {format(playerData.tournamentFeeShare)}
                                  </p>
                                </div>
                              </div>
                            )}

                            <h4 className="font-semibold text-gray-900 text-sm">
                              Game-by-Game Expenses for {playerData.player.name}
                            </h4>
                            {playerData.gameBreakdown.length === 0 ? (
                              <p className="text-sm text-gray-500 italic">No game expenses recorded</p>
                            ) : (
                              playerData.gameBreakdown.map(({ game, gameExpenses, playerShare, isSuperSub, isLate, totalGameCost }) => (
                                <div key={game.id} className="bg-white border border-gray-200 rounded-lg p-4">
                                  <div className="flex justify-between items-start mb-2">
                                    <div>
                                      <p className="font-semibold text-gray-900">{game.name}</p>
                                      <p className="text-xs text-gray-500">
                                        {formatDate(game.date)}
                                        {isSuperSub && (
                                          <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-semibold">
                                            Super Sub (50%)
                                          </span>
                                        )}
                                        {isLate && (
                                          <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">
                                            Late (+{format(game.lateFeeAmount ?? 5)})
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm text-gray-500">Player's Share:</p>
                                      <p className="text-lg font-bold text-purple-600">
                                        {format(playerShare)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="mt-2 text-xs text-gray-600 space-y-1">
                                    {gameExpenses.map(exp => (
                                      <div key={exp.id} className="flex justify-between">
                                        <span>{exp.type}: {exp.description || '-'}</span>
                                        <span className="font-semibold">{format(exp.amount)}</span>
                                      </div>
                                    ))}
                                    {isLate && (
                                      <div className="flex justify-between text-red-600">
                                        <span>Late Arrival Fee</span>
                                        <span className="font-semibold">{format(game.lateFeeAmount ?? 5)}</span>
                                      </div>
                                    )}
                                    <div className="pt-1 border-t border-gray-200 flex justify-between font-semibold">
                                      <span>Total Game Cost:</span>
                                      <span>{format(totalGameCost)}</span>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}

                            {/* Summary */}
                            <div className="bg-white border-2 border-gray-300 rounded-lg p-3 mt-3">
                              <div className="flex justify-between items-center text-sm">
                                <span className="font-semibold text-gray-700">Total Owed:</span>
                                <span className="font-bold text-orange-600">{format(playerData.totalOwed)}</span>
                              </div>
                              {playerData.paidUpfront > 0 && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-gray-600">Less: Paid Upfront:</span>
                                  <span className="text-blue-600">-{format(playerData.paidUpfront)}</span>
                                </div>
                              )}
                              {playerData.reimbursed > 0 && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-gray-600">Plus: Reimbursed:</span>
                                  <span className="text-gray-600">+{format(playerData.reimbursed)}</span>
                                </div>
                              )}
                              <div className="flex justify-between items-center text-sm pt-2 border-t mt-2">
                                <span className="font-bold text-gray-900">Net Balance:</span>
                                <span className={`font-bold ${playerData.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  {format(Math.abs(playerData.balance))} {playerData.balance > 0 ? 'Owes' : 'Credit'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* No data message */}
      {playerExpenses.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Player Data</h3>
          <p className="text-gray-600">
            {tournament
              ? `No players participated in ${tournament.name} with approved expenses.`
              : 'Select a tournament to view player expenses.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default TournamentPlayerSummary;